(() => {
  const SDK_TIMEOUT = 20000;
  const MAX_SYNC_RETRIES = 6;
  const SYNC_RETRY_DELAY = 5000;
  const SUCCESS_MESSAGE_KEY = 'smile-cart-reward-message';
  const GUEST_PREVIEW_PRODUCTS = [
    { id: -3000, name: 'SR Customized Scrunchie', pointsPrice: 3000, exchangeType: 'fixed' },
    { id: -4000, name: '$25 off coupon', pointsPrice: 4000, exchangeType: 'fixed' },
    { id: -5000, name: '20% off coupon', pointsPrice: 5000, exchangeType: 'fixed' },
    { id: -20000, name: 'Free shipping coupon', pointsPrice: 20000, exchangeType: 'fixed' }
  ];

  const read = (object, ...keys) => {
    for (const key of keys) {
      if (object && object[key] !== undefined && object[key] !== null) {
        return object[key];
      }
    }

    return undefined;
  };

  const toArray = (value) => {
    if (Array.isArray(value)) {
      return value;
    }

    const nested = read(
      value,
      'customerPointsProducts',
      'customer_points_products',
      'pointsProducts',
      'points_products',
      'items',
      'data'
    );

    return Array.isArray(nested) ? nested : [];
  };

  const normalizeProduct = (entry) => {
    const product = read(entry, 'pointsProduct', 'points_product') || entry;
    const reward = read(product, 'reward') || {};
    const pointsPrice = read(entry, 'pointsPrice', 'points_price') ??
      read(product, 'pointsPrice', 'points_price', 'variablePointsMin', 'variable_points_min');

    return {
      id: Number(read(product, 'id')),
      name:
        read(entry, 'name') ||
        read(reward, 'name') ||
        read(product, 'exchangeDescription', 'exchange_description') ||
        'Reward',
      pointsPrice: Number(pointsPrice),
      exchangeType: read(product, 'exchangeType', 'exchange_type') || 'fixed'
    };
  };

  const normalizeProducts = (value) => toArray(value)
    .map(normalizeProduct)
    .filter((product) => (
      Number.isFinite(product.id) &&
      Number.isFinite(product.pointsPrice) &&
      product.pointsPrice > 0
    ));

  const hasPublicSdk = () => (
    typeof window.Smile?.pointsProducts?.get === 'function'
  );

  const hasMemberSdk = () => (
    hasPublicSdk() &&
    typeof window.Smile?.customer?.current === 'function' &&
    typeof window.Smile?.customerPointsWallet?.get === 'function' &&
    typeof window.Smile?.customerPointsProducts?.get === 'function' &&
    typeof window.Smile?.pointsProducts?.purchase === 'function'
  );

  const waitForSmile = (memberRequired) => new Promise((resolve, reject) => {
    const startedAt = Date.now();
    let timer;

    const finish = () => {
      window.clearTimeout(timer);
      document.removeEventListener('smile-js-initialized', check);
    };

    const check = () => {
      const ready = memberRequired ? hasMemberSdk() : hasPublicSdk();

      if (ready) {
        finish();
        resolve(window.Smile);
        return;
      }

      if (Date.now() - startedAt >= SDK_TIMEOUT) {
        finish();
        const error = new Error('Rewards are taking longer than expected to load.');
        error.code = 'SMILE_SDK_TIMEOUT';
        reject(error);
        return;
      }

      timer = window.setTimeout(check, 150);
    };

    document.addEventListener('smile-js-initialized', check);
    check();
  });

  const pointsLabel = (points) => `${Number(points || 0).toLocaleString()} Points`;

  const openSmilePanel = () => {
    if (window.SmileUI?.openPanel) {
      window.SmileUI.openPanel({ deep_link: 'points_products' });
      return;
    }

    window.location.hash = 'smile-points-products';
  };

  if (!customElements.get('smile-cart-rewards')) {
    class SmileCartRewards extends HTMLElement {
      connectedCallback() {
        if (this.initializing) {
          this.startMemberStatusCheck();
          this.load();
          return;
        }

        this.initializing = true;
        this.isMember = this.dataset.customerLoggedIn === 'true';
        this.balanceElement = this.querySelector('[data-smile-balance]');
        this.controls = this.querySelector('[data-smile-controls]');
        this.select = this.querySelector('[data-smile-reward-select]');
        this.applyButton = this.querySelector('[data-smile-apply]');
        this.message = this.querySelector('[data-smile-message]');
        this.retryButton = this.querySelector('[data-smile-retry]');
        this.loginButton = this.querySelector('[data-smile-login]');
        this.syncRetries = 0;
        this.zeroBalanceRetries = 0;
        this.loadSequence = 0;

        this.select?.addEventListener('change', () => this.onSelectionChange());
        this.applyButton?.addEventListener('click', () => this.applyReward());
        this.retryButton?.addEventListener('click', () => {
          this.syncRetries = 0;
          this.zeroBalanceRetries = 0;
          this.load();
        });
        this.querySelector('[data-smile-open-panel]')?.addEventListener('click', openSmilePanel);
        this.loginButton?.addEventListener('click', (event) => {
          if (window.Shopify?.designMode || window.self !== window.top) {
            event.preventDefault();
            this.openAccount();
          }
        });

        const previousMessage = window.sessionStorage?.getItem(SUCCESS_MESSAGE_KEY);

        if (previousMessage) {
          window.sessionStorage.removeItem(SUCCESS_MESSAGE_KEY);
          this.setMessage(previousMessage, 'success');
        }

        this.startMemberStatusCheck();
        this.load();
      }

      disconnectedCallback() {
        window.clearTimeout(this.retryTimer);
        window.clearInterval(this.memberStatusTimer);
        this.memberStatusTimer = null;
        this.loadSequence += 1;
      }

      startMemberStatusCheck() {
        if (this.memberStatusTimer) return;

        this.memberStatusTimer = window.setInterval(() => {
          if (!this.isConnected || this.isMember || typeof window.Smile?.customer?.current !== 'function') return;

          try {
            if (window.Smile.customer.current()) {
              this.isMember = true;
              this.load();
            }
          } catch (error) {
            console.warn('[Smile cart rewards] Customer status is not ready yet.', error);
          }
        }, 3000);
      }

      async load() {
        const sequence = ++this.loadSequence;
        window.clearTimeout(this.retryTimer);
        this.setLoadingState();

        try {
          this.smile = await waitForSmile(false);

          if (sequence !== this.loadSequence) return;

          const currentCustomer = typeof this.smile.customer?.current === 'function'
            ? this.smile.customer.current()
            : null;
          this.isMember = Boolean(currentCustomer) || this.dataset.customerLoggedIn === 'true';

          if (this.isMember) {
            this.smile = await waitForSmile(true);
            if (sequence !== this.loadSequence) return;
            await this.loadMember(sequence);
          } else {
            await this.loadGuest(sequence);
          }
        } catch (error) {
          if (sequence !== this.loadSequence) return;

          if (this.isMember) {
            await this.renderRecoverableError(error);
          } else {
            console.warn('[Smile cart rewards] Live guest rewards could not be loaded; preview rewards remain available.', error);
            this.products = GUEST_PREVIEW_PRODUCTS;
            this.renderGuest();
          }
        }
      }

      async loadGuest(sequence) {
        const pointsProducts = await this.smile.pointsProducts.get();

        if (sequence !== this.loadSequence) {
          return;
        }

        const liveProducts = normalizeProducts(pointsProducts);
        this.products = liveProducts.length > 0 ? liveProducts : GUEST_PREVIEW_PRODUCTS;
        this.renderGuest();
      }

      async loadMember(sequence) {
        const customer = this.smile.customer.current();

        if (!customer) {
          const previewProducts = await this.loadPublicProducts();

          if (sequence !== this.loadSequence) {
            return;
          }

          this.products = previewProducts;
          this.renderSyncing();
          this.scheduleSyncRetry();
          return;
        }

        const [walletResult, customerProductsResult] = await Promise.allSettled([
          this.smile.customerPointsWallet.get(),
          this.smile.customerPointsProducts.get()
        ]);

        if (sequence !== this.loadSequence) {
          return;
        }

        const wallet = walletResult.status === 'fulfilled' ? walletResult.value : undefined;
        const walletBalance = read(wallet, 'balance', 'pointsBalance', 'points_balance');
        const customerBalance = read(customer, 'pointsBalance', 'points_balance', 'balance');
        const balance = walletBalance ?? customerBalance;

        if (balance === undefined || balance === null || !Number.isFinite(Number(balance))) {
          throw walletResult.status === 'rejected'
            ? walletResult.reason
            : new Error('Your latest points balance could not be read.');
        }

        let products;

        if (customerProductsResult.status === 'fulfilled') {
          products = normalizeProducts(customerProductsResult.value);
        } else {
          products = await this.loadPublicProducts();
        }

        if (sequence !== this.loadSequence) {
          return;
        }

        this.customer = customer;
        this.balance = Number(balance);
        this.products = products;
        this.syncRetries = 0;
        this.renderMember();
      }

      async loadPublicProducts() {
        try {
          if (!hasPublicSdk()) {
            return [];
          }

          return normalizeProducts(await window.Smile.pointsProducts.get());
        } catch (error) {
          console.warn('[Smile cart rewards] Reward preview could not be loaded.', error);
          return [];
        }
      }

      setLoadingState() {
        this.dataset.redeemState = 'loading';
        this.balanceElement.textContent = 'Checking rewards...';
        if (this.loginButton) this.loginButton.hidden = true;
        this.controls.hidden = false;
        this.controls.removeAttribute('title');
        this.select.disabled = true;
        this.select.replaceChildren(new Option('Loading available rewards...', ''));
        this.applyButton.disabled = true;
        this.applyButton.textContent = 'Loading';
        this.setRetryVisible(false);
      }

      renderGuest() {
        this.dataset.redeemState = 'guest';
        this.balanceElement.textContent = 'Sign in to redeem';
        if (this.loginButton) this.loginButton.hidden = false;
        this.renderOptions(false);
        this.select.disabled = this.products.length === 0;
        this.applyButton.disabled = false;
        this.applyButton.textContent = 'Log in to redeem';
        this.setRetryVisible(false);

        if (this.products.length === 0) {
          this.select.replaceChildren(new Option('No rewards available', ''));
          this.setMessage('No active rewards are available right now. You can still sign in to your member account.', 'notice');
        } else {
          this.setMessage('Select a reward, then log in or create a member account to redeem it.', 'notice');
        }
      }

      renderSyncing() {
        this.dataset.redeemState = 'syncing';
        this.balanceElement.textContent = 'Connecting account...';
        if (this.loginButton) this.loginButton.hidden = true;
        this.renderOptions(false);
        this.select.disabled = this.products.length === 0;
        this.applyButton.disabled = true;
        this.applyButton.textContent = 'Syncing';
        this.setRetryVisible(true);
        this.setMessage('You are signed in. Smile is finishing your rewards connection; this page will retry automatically.', 'notice');
      }

      renderMember() {
        if (this.loginButton) this.loginButton.hidden = true;
        this.balanceElement.textContent = pointsLabel(this.balance);
        this.renderOptions(true);
        this.controls.hidden = false;
        this.controls.removeAttribute('title');
        this.applyButton.disabled = true;
        this.applyButton.textContent = 'Select';
        this.setRetryVisible(false);

        if (this.products.length === 0) {
          this.dataset.redeemState = 'unavailable';
          this.select.disabled = true;
          this.select.replaceChildren(new Option('No rewards available', ''));
          this.applyButton.textContent = 'Unavailable';
          this.setMessage('No active redemption rewards are configured in Smile.', 'notice');
          return;
        }

        const affordableRewards = this.products.filter((product) => product.pointsPrice <= this.balance);

        if (affordableRewards.length === 0) {
          const minimumPoints = Math.min(...this.products.map((product) => product.pointsPrice));
          const pointsNeeded = Math.max(minimumPoints - this.balance, 0);
          const lockedMessage = `Earn ${pointsNeeded.toLocaleString()} more points to unlock your first reward.`;

          this.dataset.redeemState = 'locked';
          this.select.disabled = false;
          this.applyButton.textContent = 'Locked';
          this.controls.title = `${lockedMessage} Unavailable rewards are shown in grey.`;

          if (this.customer?.state === 'candidate' || this.balance === 0) {
            this.setMessage('If you just joined, sign-up points can take a moment to appear. We will refresh once automatically.', 'notice');
            this.scheduleZeroBalanceRefresh();
          } else if (this.message?.dataset.state !== 'success') {
            this.setMessage(lockedMessage, 'notice');
          }
        } else if (this.message?.dataset.state !== 'success') {
          this.dataset.redeemState = 'ready';
          this.select.disabled = false;
          this.clearMessage();
        } else {
          this.dataset.redeemState = 'ready';
          this.select.disabled = false;
        }
      }

      renderOptions(disableUnaffordable) {
        this.select.replaceChildren(new Option('Select your preferred discount', ''));

        this.products.forEach((product) => {
          const label = `${product.name} (${pointsLabel(product.pointsPrice)})`;
          const option = new Option(label, `${product.id}:${product.pointsPrice}`);

          option.disabled = Boolean(disableUnaffordable && product.pointsPrice > this.balance);
          option.dataset.productId = String(product.id);
          option.dataset.points = String(product.pointsPrice);
          option.dataset.exchangeType = product.exchangeType;
          this.select.add(option);
        });
      }

      async renderRecoverableError(error) {
        console.warn('[Smile cart rewards] Rewards could not be refreshed.', error);
        this.products = await this.loadPublicProducts();
        this.dataset.redeemState = 'recoverable';
        if (this.loginButton) this.loginButton.hidden = true;
        this.balanceElement.textContent = 'Try rewards again';
        this.renderOptions(false);
        this.select.disabled = this.products.length === 0;
        this.applyButton.disabled = true;
        this.applyButton.textContent = 'Retry';
        this.controls.title = 'Rewards could not be refreshed yet.';
        this.setRetryVisible(true);
        this.setMessage(
          error?.code === 'SMILE_SDK_TIMEOUT'
            ? 'Smile is still loading. Check that the Smile app embed is enabled, then try again.'
            : 'Your rewards could not be refreshed yet. Please try again.',
          'notice'
        );
      }

      scheduleSyncRetry() {
        if (this.syncRetries >= MAX_SYNC_RETRIES || !this.isConnected) {
          return;
        }

        this.syncRetries += 1;
        this.retryTimer = window.setTimeout(() => this.load(), SYNC_RETRY_DELAY);
      }

      scheduleZeroBalanceRefresh() {
        if (this.zeroBalanceRetries >= 1 || !this.isConnected) {
          return;
        }

        this.zeroBalanceRetries += 1;
        this.retryTimer = window.setTimeout(() => this.load(), 7000);
      }

      onSelectionChange() {
        this.clearMessage();

        if (!this.isMember) {
          this.applyButton.disabled = false;
          this.applyButton.textContent = 'Log in to redeem';

          if (this.select.value) {
            this.setMessage('Please log in or create a member account to redeem this reward.', 'notice');
          } else {
            this.setMessage('Select a reward, then log in or create a member account to redeem it.', 'notice');
          }

          return;
        }

        if (this.dataset.redeemState !== 'ready') {
          this.applyButton.disabled = true;
          return;
        }

        this.applyButton.disabled = !this.select.value;
      }

      openAccount() {
        const loginUrl = this.dataset.loginUrl || '/account/login';

        if (window.Shopify?.designMode || window.self !== window.top) {
          window.open(loginUrl, '_blank', 'noopener,noreferrer');
          return;
        }

        window.location.assign(loginUrl);
      }

      async applyReward() {
        if (!this.isMember) {
          this.openAccount();
          return;
        }

        const option = this.select.selectedOptions[0];

        if (this.dataset.redeemState !== 'ready' || !option?.dataset.productId || option.disabled) {
          return;
        }

        this.applyButton.disabled = true;
        this.applyButton.setAttribute('aria-busy', 'true');
        this.applyButton.textContent = 'Applying...';
        this.clearMessage();

        try {
          const productId = Number(option.dataset.productId);
          const points = Number(option.dataset.points);
          const purchaseOptions = option.dataset.exchangeType === 'variable'
            ? { pointsToSpend: points }
            : undefined;
          const purchaseResult = await this.smile.pointsProducts.purchase(productId, purchaseOptions);
          const purchase = read(purchaseResult, 'pointsPurchase', 'points_purchase') || purchaseResult;
          const fulfillment = read(purchase, 'rewardFulfillment', 'reward_fulfillment') || {};
          const code = read(fulfillment, 'code');

          if (!code) {
            throw new Error('The reward was redeemed. Open Rewards to view your discount code.');
          }

          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ discount: code })
          });

          if (!response.ok) {
            throw new Error(`Reward redeemed. Use discount code ${code} at checkout.`);
          }

          window.sessionStorage?.setItem(SUCCESS_MESSAGE_KEY, 'Reward applied to your cart.');
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          await this.load();
          this.setMessage('Reward applied to your cart.', 'success');
        } catch (error) {
          this.setMessage(error?.message || 'This reward could not be applied. Please try again.', 'error');
        } finally {
          this.applyButton.removeAttribute('aria-busy');
          this.applyButton.textContent = 'Select';
          this.applyButton.disabled = !this.select.value;
        }
      }

      setRetryVisible(visible) {
        if (this.retryButton) {
          this.retryButton.hidden = !visible;
        }
      }

      setMessage(text, state) {
        if (!this.message) {
          return;
        }

        this.message.textContent = text;
        this.message.dataset.state = state;
      }

      clearMessage() {
        if (!this.message) {
          return;
        }

        this.message.textContent = '';
        delete this.message.dataset.state;
      }
    }

    customElements.define('smile-cart-rewards', SmileCartRewards);
  }
})();
